import type { MapGeometry } from "../shared/types";
import { venueMapBounds } from "./venueMapBounds";
import {
  MAP_3D_PITCH,
  MAP_3D_ROTATION,
  projectMapDepth,
  projectMapPoint,
} from "./venueMapProjection";

/** Normalized map anchor, source-unit elevation and number of upright pins. */
export type VenueMapFitPin = {
  x: number;
  y: number;
  z: number;
  count: number;
};

type FitPoint = {
  x: number;
  y: number;
  depth: number;
  left: number;
  right: number;
  top: number;
  bottom: number;
};

/**
 * Fit the floor and its actual callouts without reserving a full pin around
 * every floor corner. Translation stays in the camera plane, preserving the
 * optical centre and perspective used by gestures and both renderers.
 */
export function fitVenueMap(
  width: number,
  height: number,
  aspect: number,
  rotation: number,
  pitch: number,
  pins: readonly VenueMapFitPin[],
  geometry: MapGeometry,
): { imageWidth: number; panX: number; panY: number } {
  if (
    !Number.isFinite(width) ||
    !Number.isFinite(height) ||
    width <= 0 ||
    height <= 0
  ) {
    return { imageWidth: 0, panX: 0, panY: 0 };
  }
  const sourceWidth = geometry.width;
  const bounds = venueMapBounds(geometry);
  const camera = {
    sourceWidth: geometry.width,
    sourceHeight: geometry.height,
    sourceElevation: bounds.elevation,
    imageWidth: sourceWidth,
    imageHeight:
      sourceWidth /
      (Number.isFinite(aspect) && aspect > 0
        ? aspect
        : geometry.width / geometry.height),
    stageWidth: 0,
    stageHeight: 0,
    rotation: Number.isFinite(rotation) ? rotation : MAP_3D_ROTATION,
    pitch: Number.isFinite(pitch)
      ? Math.max(0, Math.min(70, pitch))
      : MAP_3D_PITCH,
    perspective: true,
  };
  // Very small, transient ResizeObserver measurements cannot retain an 8px
  // edge on both sides. Keep their geometry finite until the next measurement.
  const margin = Math.min(8, width / 4, height / 4);
  const project = (point: { x: number; y: number; z?: number }): FitPoint => {
    const projected = projectMapPoint(camera, point);
    return {
      x: projected.x / sourceWidth,
      y: projected.y / sourceWidth,
      depth: projectMapDepth(camera, point),
      left: margin,
      right: margin,
      top: margin,
      bottom: margin,
    };
  };
  const floorPoints = bounds.points.map(project);
  // Entrance lettering stays upright and scales in source units, independently
  // of its anchor's perspective factor. A small conservative text box avoids
  // loading the outline atlas into the camera/gesture code.
  const entrancePoints = geometry.entrances.flatMap((entrance) => {
    const anchor = project({
      x: entrance.x / geometry.width,
      y: entrance.y / geometry.height,
    });
    return [-4.5, 8 + entrance.label.length * 6].flatMap((dx) =>
      [-10, 5].map((dy) => ({
        ...anchor,
        x: anchor.x + dx / sourceWidth,
        y: anchor.y + dy / sourceWidth,
      })),
    );
  });
  const basePoints = [...floorPoints, ...entrancePoints];
  const pinPoints = pins.flatMap((pin) => {
    if (
      !Number.isFinite(pin.x) ||
      !Number.isFinite(pin.y) ||
      !Number.isFinite(pin.z) ||
      !Number.isFinite(pin.count) ||
      pin.count < 1
    )
      return [];
    const count = Math.ceil(pin.count);
    const columns = Math.min(3, count);
    const rows = Math.ceil(count / 3);
    const pinWidth = columns * 44 + (columns - 1) * 3 + (count > 1 ? 6 : 0);
    const pinHeight = rows * 44 + (rows - 1) * 3 + (count > 1 ? 14 : 8);
    return {
      ...project(pin),
      left: pinWidth / 2 + 6,
      right: pinWidth / 2 + 6,
      top: pinHeight + 6,
      bottom: 6,
    };
  });
  const intervals = (imageWidth: number, points: readonly FitPoint[]) => {
    let minX = -Infinity;
    let maxX = Infinity;
    let minY = -Infinity;
    let maxY = Infinity;
    for (const point of points) {
      minX = Math.max(
        minX,
        (-width / 2 + point.left - point.x * imageWidth) / point.depth,
      );
      maxX = Math.min(
        maxX,
        (width / 2 - point.right - point.x * imageWidth) / point.depth,
      );
      minY = Math.max(
        minY,
        (-height / 2 + point.top - point.y * imageWidth) / point.depth,
      );
      maxY = Math.min(
        maxY,
        (height / 2 - point.bottom - point.y * imageWidth) / point.depth,
      );
    }
    return { minX, maxX, minY, maxY, fits: minX <= maxX && minY <= maxY };
  };
  let points = [...basePoints, ...pinPoints];
  // Fixed 44px touch targets cannot fit in a smaller viewport at any zoom.
  // Still frame the floor sensibly while the existing pan limits keep the
  // callouts reachable; a subsequent full-size measurement fits them normally.
  if (!intervals(0, points).fits) points = basePoints;
  let lower = 0;
  let upper = Math.max(width, height);
  while (intervals(upper, points).fits) upper *= 2;
  for (let attempt = 0; attempt < 40; attempt++) {
    const middle = (lower + upper) / 2;
    if (intervals(middle, points).fits) lower = middle;
    else upper = middle;
  }
  const framing = intervals(lower, points);
  return {
    imageWidth: lower,
    panX: (framing.minX + framing.maxX) / 2,
    panY: (framing.minY + framing.maxY) / 2,
  };
}
